<template>
  <base-editor-page>
    <p slot="description">
      Change the framework of the Inbox that your participants will experience. You are able to
      adjust the type of timing system used in your Inbox, as well as how much time you’d like to
      give your participants to complete the exercise. More gadgets will be added in the future.
    </p>

    <template v-slot:body>
      <div class="row">
        <div class="col-md-6 col-sm-12 col-lg-12">
          <form class="form-horizontal">
            <div class="form-group mb-0">
              <label class="col-sm-5 col-lg-2" for="timer-settings">Timer Settings: </label>
              <div class="col-sm-7 col-lg-2">
                <select
                  v-model="localTimerType"
                  @change="handleUpdate"
                  class="form-control"
                  id="timer-settings"
                >
                  <option :value="'normal'">Countdown</option>
                  <option :value="'showTimerAsClock'">Clock</option>
                  <option :value="'hideTimer'">No Timer</option>
                </select>
              </div>
            </div>
            <div v-show="localTimerType === 'normal'" class="mb-15 mt-5">
              During the Inbox, a timer will appear at the top of the interface. It will begin
              counting down from the Inbox length that you input below.
            </div>
            <div v-show="localTimerType === 'showTimerAsClock'" class="mb-15 mt-5">
              During the Inbox, the timer will appear as a clock, starting the Inbox at the start
              time that you input below. The Inbox will complete once the clock has moved forward
              the Inbox length time. Time will pass at a normal rate (1 minute = 1 minute).
            </div>
            <div v-show="localTimerType === 'hideTimer'" class="mb-15 mt-5">
              During the Inbox, the timer will not appear in the Inbox interface. The user
              experience is not impacted in any way.
            </div>

            <div v-show="localTimerType === 'showTimerAsClock'" class="form-group">
              <label class="col-sm-5 col-lg-2">Start Time: </label>
              <div class="col-sm-7 col-lg-2">
                <select v-model="localStartTime" @change="handleUpdate" class="form-control">
                  <option v-for="{ label, val } in startTimeOptions" :value="val" :key="val">
                    {{ label }}
                  </option>
                </select>
              </div>
            </div>

            <div v-show="localTimerType !== 'hideTimer'" class="form-group">
              <div class="col-sm-5 col-lg-2">
                <span class="custom-label" for="version-length"
                  >Recommended Time to Complete:
                </span>
                <popover
                  role="time-to-complete-popover"
                  :content="tooltips.builderPage.timeToComplete"
                ></popover>
              </div>
              <div class="col-sm-7 col-lg-2">
                <div class="input-group">
                  <input
                    class="form-control"
                    data-testid="time-to-complete-cy"
                    type="number"
                    min="5"
                    max="320"
                    v-model.number="localTimer"
                    @input="debouncedHandleUpdate"
                    id="version-length"
                  />
                  <div class="input-group-addon">minutes</div>
                </div>
              </div>
            </div>

            <div class="form-group">
              <label class="col-sm-5 col-lg-2" for="scoring-method">Scoring Method: </label>
              <div class="col-sm-7 col-lg-2">
                <select
                  v-model="localScoringMethod"
                  @change="handleUpdate"
                  class="form-control"
                  id="scoring-method"
                >
                  <option :value="'percentile'">Percentile</option>
                  <option :value="'percentage'">Percentage</option>
                </select>
              </div>
            </div>

            <p v-show="localScoringMethod === 'percentage'">
              Percentage scoring evaluates the points a participant earns against the number of
              possible points. The participant’s feedback report will calculate and display
              performance based on the percentage earned.
            </p>

            <p v-show="localScoringMethod === 'percentile'">
              Percentile scoring evaluates the participant’s performance compared to the entire
              population of results. The participant’s feedback report will calculate and display
              performance based on the percentile results. Note: as the data set establishes -
              results will fluctuate. A participant database of 200+ results will provide a more
              accurate result immediately after completion.
            </p>

            <div class="form-group">
              <label class="col-sm-5 col-lg-2" for="onboarding">User Onboarding: </label>
              <div class="col-sm-7 col-lg-4">
                <span> {{ onboardingChoice }} </span>
                <button @click.prevent="openOnboarding" class="btn btn-light ml-5">
                  <span class="fas fa-cog"></span> Edit Onboarding
                </button>
              </div>
            </div>

            <template>
              <div class="form-group">
                <label class="col-sm-5 col-lg-2" role="theme-label-text" for="theme">Theme: </label>
                <div class="col-sm-7 col-lg-4">
                  <span> {{ themeChoice }} </span>
                  <button
                    @click.prevent="showTheme"
                    data-testid="edit-theme-btn"
                    class="btn btn-light ml-5"
                  >
                    <span class="fas fa-cog"></span> Edit Theme
                  </button>
                </div>
              </div>

              <theme-modal @saveTheme="saveTheme" :theme="theme" />
            </template>
            <div class="form-group">
              <label class="col-sm-5 col-lg-2" role="reshuffling-label-text" for="reshuffling">
                Shuffle Response Order:
              </label>
              <span class="ml-15">Off/On </span>
              <div class="col-sm-7 col-lg-10" style="margin-top: -20px">
                <v-switch
                  style="zoom: 0.9; padding-left: 65px"
                  :selected="isRandomizeAnswers"
                  data-testid="reshuffling-toggle"
                  @input="
                    (val) => {
                      handleRandomizeAnsToggle(val);
                    }
                  "
                >
                </v-switch>
              </div>
              <div class="col-md-12">
                <p>
                  Shuffling will add subtle variation to the participant experience. Authors who are
                  building assessment inboxes may consider turning this gadget on to reduce the risk
                  of answer sharing.
                </p>
              </div>
            </div>
          </form>
        </div>
      </div>
      <onboarding-modal
        :onboardTemplateKey="onboardTemplateKey"
        :customPageKeysArray="customPageKeysArray"
      />
    </template>
  </base-editor-page>
</template>

<script>
  import BaseEditorPage from '@authoringPlatform/shared/BaseEditorPage.vue';
  import ThemeModal from './ThemeModal';
  import OnboardingModal from './OnboardingModal';
  import { mapActions, mapState } from 'vuex';
  import debounce from 'lodash/debounce';
  import Switch from '@shared/switch.vue';
  import { getStartTimeOptions } from '@utils/sharedUtils';

  export default {
    name: 'Gadgets',

    components: {
      BaseEditorPage,
      ThemeModal,
      OnboardingModal,
      'v-switch': Switch,
    },

    data() {
      return {
        localTimerType: 'normal',
        localScoringMethod: 'percentile',
        isRandomizeAnswers: false,
        localTimer: 0,
        localStartTime: 0,
        startTimeOptions: getStartTimeOptions(),
        allowReEntryInbox: false,
      };
    },

    created() {
      const { showTimerAsClock, hideTimer, startTime, scoringMethod, allowReEntryInbox } =
        this.versionConfig;
      if (showTimerAsClock) {
        this.localTimerType = 'showTimerAsClock';
        this.localStartTime = startTime;
      } else if (hideTimer) {
        this.localTimerType = 'hideTimer';
      } else {
        this.localTimerType = 'normal';
      }
      this.localScoringMethod = scoringMethod || 'percentile';
      this.localTimer = this.timer;
      this.allowReEntryInbox = allowReEntryInbox;
      if (this.versionConfig.isRandomizeAnswers !== undefined) {
        this.isRandomizeAnswers = this.versionConfig.isRandomizeAnswers;
      }
    },

    methods: {
      ...mapActions(['SET_MILESTONE']),
      ...mapActions('editMode/version', ['updateVersion']),
      debouncedHandleUpdate: debounce(function () {
        this.handleUpdate();
      }, 1000),
      async handleRandomizeAnsToggle(val) {
        if (val !== this.isRandomizeAnswers) {
          this.isRandomizeAnswers = val;
          this.handleUpdate();
        }
      },

      async handleUpdate() {
        this.SET_MILESTONE({
          message: 'Adjust a Gadget',
          tooltipDesc: this.tooltips.milestones['9'],
          timeout: 8000,
          milestoneKey: 9,
        });

        const payload = {
          timer: this.localTimer,
          versionConfigJson: {
            ...this.versionConfig,
            ...(this.localTimerType === 'showTimerAsClock'
              ? { showTimerAsClock: true, startTime: this.localStartTime }
              : { showTimerAsClock: false }),
            hideTimer: this.localTimerType === 'hideTimer',
            scoringMethod: this.localScoringMethod,
            isRandomizeAnswers: this.isRandomizeAnswers,
          },
        };

        await this.updateVersion({
          versionKey: this.$route.params.resourceKey,
          payload,
        });
      },
      showTheme() {
        $('#theme-modal').modal('show');
      },

      openOnboarding() {
        $('#onboarding-modal').modal('show');
      },

      async saveTheme(result) {
        this.theme = result;
        await this.handleUpdate();
      },
    },

    computed: {
      ...mapState('user', ['isAdmin']),
      ...mapState('editMode/version', [
        'onboardTemplateKey',
        'theme',
        'versionConfig',
        'timer',
        'customPageKeysArray',
      ]),

      themeChoice() {
        return this.theme.isPreset ? 'Default' : 'Custom';
      },
      onboardingChoice() {
        if (this.onboardTemplateKey === 1) {
          return 'Standard';
        } else if (this.onboardTemplateKey === 2) {
          return 'Lite';
        } else {
          return 'Custom';
        }
      },
    },
  };
</script>
<style lang="scss" scoped>
  .custom-label {
    font-size: 16.32px;
    font-weight: 700;
  }
</style>
